import {
  Order,
  OrderLine,
  OrderStatus,
  Country,
  User,
  Location,
  Vessel,
  Company,
} from './models/models';
import OrderService from '../services/order.service';

const ordersQueryProps = {
  filters: {},
  sort: [],
  page: 1,
  limit: 30,
  group: 'uncompleted',
};

export default {
  namespaced: true,
  state: {
    orderId: null,
    totalOrders: null,
    ordersQueries: {},
    transactionsQuery: {
      filters: {},
      sort: [],
      page: 1,
      limit: 30,
    },
    prevStatusCode: null,
    isSendOrderModalOpen: null,
    isStatusCodeChanged: null,
    totalTransactions: null,
    dates: {
      from: null,
      to: null,
    },
  },
  mutations: {
    orderId(state, orderId) {
      state.orderId = orderId;
    },
    totalOrders(state, totalOrders) {
      state.totalOrders = totalOrders;
    },
    totalTransactions(state, totalTransactions) {
      state.totalTransactions = totalTransactions;
    },
    initOrdersQueries(state) {
      const types = ['purchase', 'transfer', 'adjustment', 'treatment', 'delivery', 'sale', 'job'];
      types.forEach((type) => {
        state.ordersQueries[type] = {
          ...ordersQueryProps,
          type,
        };
      });
    },
    ordersQueryProps(state, payload) {
      state.ordersQueries[payload.type] = Object
        .assign({}, state.ordersQueries[payload.type], payload.props);
      state.ordersQueries = { ...state.ordersQueries }; // this is to trigger reference change
    },
    transactionsQueryProps(state, props) {
      state.transactionsQuery = Object.assign({}, state.transactionsQuery, props);
    },
    dateProps(state, props) {
      state.dates = Object.assign({}, state.dates, props);
    },
    isSendOrderModalOpen(state, payload) {
      state.isSendOrderModalOpen = payload.isOpen;
      state.isStatusCodeChanged = payload.hasOwnProperty('isStatusCodeChanged') ? payload.isStatusCodeChanged : false;
    },
    prevStatusCode(state, status) {
      state.prevStatusCode = status;
    },
  },
  actions: {
    init({ dispatch, commit }) {
      commit('initOrdersQueries');
      dispatch('getCountries');
      dispatch('getStatuses');
    },
    newOrder({ commit }, type) {
      Order.new()
        .then((order) => {
          order.$update({
            type_code: type,
          });
          commit('orderId', order.id);
        });
    },
    getOrders({ commit, state }, orderType) {
      const orderProps = state.ordersQueries[orderType];
      orderProps.dates = state.dates;
      return OrderService.getOrders(orderProps)
        .then((res) => {
          commit('totalOrders', res.total);
          // don't update the order that is open
          Order.create({
            data: res.data,
            insertOrUpdate: ['order_statuses', 'order_lines', 'products', 'currencies', 'packages',
              'treatment_categories', 'countries', 'companies', 'users'],
          });
        });
    },
    getTransactions({ commit, state }) {
      return OrderService.getTransactions(state.transactionsQuery)
        .then((res) => {
          commit('totalTransactions', res.total);
          OrderLine.create({
            data: res.data,
            insertOrUpdate: ['order_line_statuses'],
          });
        });
    },
    getOrderLinePrice(store, props) {
      return OrderService.getOrderLinePrice(props)
        .then((res) => {
          OrderLine.update({
            where: props.order_line_id,
            data: { price: res.price },
          });
        });
    },
    sendOrder({ state }, params) {
      return OrderService.sendOrder(state.orderId, params);
    },
    generateInvoice({ state }) {
      return OrderService.generateInvoice(state.orderId)
        .then((res) => res);
    },
    getOrderPdf(store, id) {
      return OrderService.getOrderPdf(id);
    },
    getCountries() {
      OrderService.getCountries()
        .then((countries) => Country.create({ data: countries }));
    },
    getStatuses() {
      OrderService.getStatuses()
        .then((statuses) => OrderStatus.create({ data: statuses }));
    },
    updateOrderProps({ state, dispatch }, props) {
      if (props.hasOwnProperty('supplier_contact_user')) {
        User.insert({ data: props.supplier_contact_user })
          .then(() => {
            dispatch('updateOrderProps', {
              supplier_company_id: props.supplier_contact_user.company
                ? props.supplier_contact_user.company.id : null,
            });
          });
      }
      if (props.hasOwnProperty('client_contact_user')) {
        User.insert({ data: props.client_contact_user })
          .then(() => {
            dispatch('updateOrderProps', {
              client_company_id: props.client_contact_user.company
                ? props.client_contact_user.company.id : null,
            });
          });
      }
      if (props.hasOwnProperty('vessel')) {
        Vessel.insert({ data: props.vessel });
      }
      if (props.hasOwnProperty('job_location')) {
        Location.insert({ data: props.job_location });
      }
      if (props.hasOwnProperty('loading_location')) {
        Location.insert({ data: props.loading_location });
      }
      if (props.hasOwnProperty('logistics_contact_user')) {
        User.insert({ data: props.logistics_contact_user })
          .then(() => {
            dispatch('updateOrderProps', {
              logistics_company_id: props.logistics_contact_user.company
                ? props.logistics_contact_user.company.id : null,
            });
          });
      }
      if (props.hasOwnProperty('supplier_company')) {
        Company.insert({ data: props.supplier_company })
          .then(() => {
            dispatch('updateOrderProps', {
              supplier_contact_user_id: props.supplier_company.default_contact_user
                ? props.supplier_company.default_contact_user.id : null,
            });
          });
      }
      if (props.hasOwnProperty('client_company')) {
        Company.insert({ data: props.client_company })
          .then(() => {
            dispatch('updateOrderProps', {
              client_contact_user_id: props.client_company.default_contact_user
                ? props.client_company.default_contact_user.id : null,
            });
          });
      }
      if (props.hasOwnProperty('logistics_company')) {
        Company.insert({ data: props.logistics_company })
          .then(() => {
            dispatch('updateOrderProps', {
              logistics_contact_user_id: props.logistics_company.default_contact_user
                ? props.logistics_company.default_contact_user.id : null,
            });
          });
      }
      return Order.update({
        where: state.orderId,
        data: props,
      });
    },
    saveOrder({ dispatch, getters }) {
      return OrderService.createOrder(getters.order)
        .then((res) => {
          if (res.hasOwnProperty('order_id')) {
            dispatch('getOrder', res.order_id);
          }
          return res;
        });
    },
    updateOrder({ getters, dispatch }) {
      return OrderService.updateOrder(getters.order)
        .then((res) => {
          if (res.hasOwnProperty('order_id')) {
            dispatch('getOrder', res.order_id);
          }
          return res;
        });
    },
    deleteOrder({ getters }) {
      return OrderService.deleteOrder(getters.order)
        .then((res) => {
          if (res.hasOwnProperty('order_id')) {
            Order.delete(res.order_id);
          }
          return res;
        });
    },
    async getOrder({ commit }, id) {
      const res = await OrderService.getOrder(id);
      await Order.insert({
        data: [res],
        insertOrUpdate: ['packages', 'treatment_categories'],
      });
      commit('orderId', res.id);
      return res;
    },
  },
  getters: {
    order(state) {
      if (!state.orderId) {
        return null;
      }
      return Order.query()
        .withAllRecursive()
        .find(state.orderId);
    },
    orders() {
      return Order.query()
        .withAllRecursive()
        .all();
    },
    countries() {
      return Country.all();
    },
    orderStatuses() {
      return OrderStatus.all();
    },
    orderLines(state, getters) {
      return getters.order.order_lines;
    },
    transactions() {
      return OrderLine.query()
        .withAllRecursive()
        .all();
    },
    totalAmount(state, getters) {
      if (!state.orderId) {
        return null;
      }
      let totalCost = 0;
      getters.order.order_lines.forEach((orderLine) => {
        totalCost += orderLine.quantity * orderLine.price;
        if (orderLine.children) {
          orderLine.children.forEach((orderLineCustomization) => {
            totalCost += orderLineCustomization.quantity * orderLineCustomization.price;
          });
        }
      });
      return totalCost;
    },
    vatPct(state, getters) {
      if (getters.order.user && getters.order.user.company) {
        return getters.order.user.company.is_vat_included ? 20 : 0;
      }
      return 0;
    },
    vatAmount(state, getters) {
      return getters.totalAmount * (getters.vatPct / 100);
    },
    totalAmountWithVat(state, getters) {
      return getters.totalAmount + getters.vatAmount;
    },
  },
};
